import { Component, ElementRef, Inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, finalize, interval, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { VerificationCodeConfig } from '../../validation-code/model/verification-code.model';
import { ValidationCodeService } from '../../validation-code/service/validation-code.service';
import { ContactOtpDialogResult, ContactOtpState } from '../model/contact-otp-state';

const NOTICE_DURATION_MS = 4000;

@Component({
  selector: 'app-contact-otp-dialog',
  templateUrl: './contact-otp-dialog.component.html',
  styleUrls: ['./contact-otp-dialog.component.scss'],
})
export class ContactOtpDialogComponent implements OnInit, OnDestroy {
  readonly digits = [0, 1, 2, 3, 4, 5] as const;
  readonly config: VerificationCodeConfig = environment.otp;
  readonly noticePositions: ConnectedPosition[] = [{
    originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -48,
  }];
  readonly codeForm = new FormGroup({
    code0: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^[a-zA-Z0-9]$/)] }),
    code1: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^[a-zA-Z0-9]$/)] }),
    code2: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^[a-zA-Z0-9]$/)] }),
    code3: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^[a-zA-Z0-9]$/)] }),
    code4: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^[a-zA-Z0-9]$/)] }),
    code5: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.pattern(/^[a-zA-Z0-9]$/)] }),
  });
  busy = false;
  validating = false;
  resending = false;
  error = '';
  notice = '';
  submitted = false;
  expiresIn = 0;
  resendIn = 0;
  private noticeHideAt = 0;
  private readonly destroyed = new Subject<void>();

  @ViewChildren('codeInput') inputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public state: ContactOtpState,
    private readonly dialog: MatDialogRef<ContactOtpDialogComponent, ContactOtpDialogResult>,
    private readonly service: ValidationCodeService,
    private readonly spinner: NgxSpinnerService,
  ) {}

  get isPhone(): boolean {
    return this.state.channel === 'phone';
  }

  get deliveryLabel(): string {
    return this.isPhone ? 'SMS' : 'email';
  }

  get displayedRecipient(): string {
    const phone = this.state.phoneDisplay;
    return this.isPhone && phone ? `${phone.countryCode} ${phone.nationalNumber}` : this.state.recipient;
  }

  get canVerify(): boolean {
    return !this.busy && this.expiresIn > 0 &&
      (this.state.status === 'code' || this.state.status === 'incorrect');
  }

  get canResend(): boolean {
    return !this.busy && this.resendIn === 0 &&
      this.state.status !== 'confirm' && this.state.status !== 'verified';
  }

  ngOnInit(): void {
    this.codeForm.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
      if (!this.canVerify) {
        return;
      }
      if (this.state.status === 'incorrect') {
        this.state.status = 'code';
        this.submitted = false;
      }
      if (this.codeForm.valid) {
        this.verify();
      }
    });
    this.updateTimers();
    interval(1000).pipe(takeUntil(this.destroyed)).subscribe(() => this.updateTimers());
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  send(): void {
    if (!this.busy && this.state.status === 'confirm') {
      this.sendCode();
    }
  }

  verify(): void {
    this.submitted = true;
    this.updateTimers();
    if (!this.canVerify || this.codeForm.invalid) {
      return;
    }
    const state = this.state;
    const { recipient, channel, expiresAt } = state;
    const code = this.digits.map(index => this.codeForm.controls[`code${index}`].value).join('');
    this.request(this.service.validateCode({ code, email: recipient }), response => {
      if (this.state !== state || state.recipient !== recipient || state.channel !== channel ||
        state.expiresAt !== expiresAt) {
        return;
      }
      this.updateTimers();
      if (state.status !== 'code' && state.status !== 'incorrect') {
        return;
      }
      if (response === true) {
        this.state.status = 'verified';
      } else if (response === false) {
        if (this.config.maxAttempts !== null) {
          this.state.remainingAttempts = Math.max(0, (this.state.remainingAttempts ?? this.config.maxAttempts) - 1);
        }
        this.state.status = this.state.remainingAttempts === 0 ? 'blocked' : 'incorrect';
        if (this.state.status === 'blocked') {
          this.state.resendAt = Date.now() + this.config.resendCooldownSeconds * 1000;
        }
        if (this.state.status === 'incorrect') {
          this.codeForm.reset(undefined, { emitEvent: false });
          this.submitted = false;
        }
        this.updateTimers();
      }
    }, true);
  }

  resend(): void {
    this.updateTimers();
    if (this.canResend) {
      this.sendCode();
    }
  }

  edit(): void {
    if (this.busy) {
      return;
    }
    this.state.status = 'confirm';
    delete this.state.expiresAt;
    delete this.state.resendAt;
    delete this.state.remainingAttempts;
    this.dialog.close({ action: 'edit' });
  }

  complete(): void {
    if (this.state.status === 'verified') {
      this.dialog.close({ action: 'verified' });
    }
  }

  onInput(index: typeof this.digits[number]): void {
    if (this.canVerify && this.codeForm.controls[`code${index}`].valid) {
      this.inputs.get(index + 1)?.nativeElement.focus();
    }
  }

  onKeydown(event: KeyboardEvent, index: typeof this.digits[number]): void {
    if (!this.canVerify) {
      return;
    }
    if (event.key === 'ArrowLeft' || (event.key === 'Backspace' && !this.codeForm.controls[`code${index}`].value)) {
      this.inputs.get(index - 1)?.nativeElement.focus();
    } else if (event.key === 'ArrowRight') {
      this.inputs.get(index + 1)?.nativeElement.focus();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    if (!this.canVerify) {
      return;
    }
    const code = event.clipboardData?.getData('text') ?? '';
    if (!/^[a-zA-Z0-9]{6}$/.test(code)) {
      this.error = 'Ingresá los 6 caracteres del código recibido, sin espacios.';
      return;
    }
    this.error = '';
    this.codeForm.setValue({
      code0: code[0], code1: code[1], code2: code[2], code3: code[3], code4: code[4], code5: code[5],
    });
  }

  formatTime(seconds: number): string {
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
  }

  private sendCode(): void {
    const isResend = this.state.status !== 'confirm';
    this.resending = isResend;
    const deliveryLabel = this.isPhone ? 'SMS' : 'email';
    this.request(this.service.sendCode({
      contact: this.state.recipient,
      type: this.isPhone ? 'SMS' : 'EMAIL',
    }), () => {
      const now = Date.now();
      this.state.status = 'code';
      this.state.expiresAt = now + this.config.expirationMinutes * 60 * 1000;
      this.state.resendAt = now + this.config.resendCooldownSeconds * 1000;
      if (this.config.maxAttempts !== null) {
        this.state.remainingAttempts = this.config.maxAttempts;
      } else {
        delete this.state.remainingAttempts;
      }
      this.codeForm.reset(undefined, { emitEvent: false });
      this.submitted = false;
      this.notice = isResend
        ? `Te enviamos un código nuevo por ${deliveryLabel}.`
        : '';
      this.noticeHideAt = now + NOTICE_DURATION_MS;
      this.updateTimers();
    });
  }

  private request<T>(operation: Observable<T>, onSuccess: (response: T) => void, validating = false): void {
    let succeeded = false;
    this.busy = true;
    this.syncCodeInputState();
    this.validating = validating;
    this.error = '';
    this.notice = '';
    if (!validating) {
      this.spinner.show();
    }
    operation.pipe(
      takeUntil(this.destroyed),
      finalize(() => {
        if (!validating) {
          this.spinner.hide();
        }
        const restoreFocus = succeeded && (this.resending || (validating && this.state.status === 'incorrect'));
        this.busy = false;
        this.validating = false;
        this.resending = false;
        this.syncCodeInputState();
        if (restoreFocus && this.canVerify) {
          this.inputs?.first?.nativeElement.focus();
        }
      }),
    ).subscribe({
      next: response => {
        onSuccess(response);
        succeeded = true;
      },
      error: (error: unknown) => {
        this.error = error instanceof Error ? error.message : 'No pudimos completar la verificación. Intentá nuevamente.';
      },
    });
  }

  private updateTimers(): void {
    const now = Date.now();
    this.expiresIn = Math.max(0, Math.ceil(((this.state.expiresAt ?? now) - now) / 1000));
    this.resendIn = Math.max(0, Math.ceil(((this.state.resendAt ?? now) - now) / 1000));
    if (this.notice && now >= this.noticeHideAt) {
      this.notice = '';
    }
    // These timers describe the UI window, not the backend's security policy.
    if (this.expiresIn === 0 && (this.state.status === 'code' || this.state.status === 'incorrect')) {
      this.state.status = 'expired';
    }
    this.syncCodeInputState();
  }

  private syncCodeInputState(): void {
    if (this.canVerify && this.codeForm.disabled) {
      this.codeForm.enable({ emitEvent: false });
    } else if (!this.canVerify && this.codeForm.enabled) {
      this.inputs?.forEach(input => input.nativeElement.blur());
      this.codeForm.disable({ emitEvent: false });
    }
  }
}
